/**
 * Money as integer minor units (cents) tagged with an ISO 4217 currency.
 *
 * Amounts never pass through floating-point arithmetic: ratios are applied
 * with BigInt maths and an explicit rounding mode, and allocation uses the
 * largest-remainder method so splits always add back up to the original.
 */

/** Minor-unit exponent per supported currency. Add currencies here as markets open. */
const CURRENCY_EXPONENTS = {
  AUD: 2,
  NZD: 2,
  GBP: 2,
  EUR: 2,
  USD: 2,
} as const

export type CurrencyCode = keyof typeof CURRENCY_EXPONENTS

export interface Money {
  /** Integer number of minor units, e.g. cents. */
  readonly amount: number
  readonly currency: CurrencyCode
}

/**
 * `half-up` rounds halves away from zero (the usual reading of "round to the
 * nearest cent"); `half-even` is banker's rounding.
 */
export type RoundingMode = "half-up" | "half-even"

export class InvalidMoneyError extends Error {
  override name = "InvalidMoneyError"
}

export class CurrencyMismatchError extends Error {
  override name = "CurrencyMismatchError"

  // Explicit fields, not parameter properties: Node's type stripping (vectors build) cannot run those.
  readonly left: CurrencyCode
  readonly right: CurrencyCode

  constructor(left: CurrencyCode, right: CurrencyCode) {
    super(`Cannot combine ${left} with ${right}`)
    this.left = left
    this.right = right
  }
}

export function isCurrencyCode(value: string): value is CurrencyCode {
  return Object.hasOwn(CURRENCY_EXPONENTS, value)
}

export function currencyExponent(currency: CurrencyCode): number {
  return CURRENCY_EXPONENTS[currency]
}

export function money(amount: number, currency: CurrencyCode): Money {
  if (!isCurrencyCode(currency)) {
    throw new InvalidMoneyError(`Unsupported currency: ${String(currency)}`)
  }
  if (!Number.isSafeInteger(amount)) {
    throw new InvalidMoneyError(`Amount must be a safe integer of minor units, got ${amount}`)
  }
  // `amount + 0` turns -0 into 0 so equality checks behave
  return Object.freeze({ amount: amount + 0, currency })
}

export function zero(currency: CurrencyCode): Money {
  return money(0, currency)
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new CurrencyMismatchError(a.currency, b.currency)
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b)
  return money(a.amount + b.amount, a.currency)
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b)
  return money(a.amount - b.amount, a.currency)
}

export function negate(value: Money): Money {
  return money(-value.amount, value.currency)
}

/** Sums values; the currency is required so an empty list still has one. */
export function sum(values: readonly Money[], currency: CurrencyCode): Money {
  return values.reduce((total, value) => add(total, value), zero(currency))
}

export function isZero(value: Money): boolean {
  return value.amount === 0
}

export function compare(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b)
  return a.amount < b.amount ? -1 : a.amount > b.amount ? 1 : 0
}

function assertInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value)) {
    throw new InvalidMoneyError(`${label} must be a safe integer, got ${value}`)
  }
}

/** Integer division of `numerator / denominator` with the given rounding. */
function divideRounded(numerator: bigint, denominator: bigint, mode: RoundingMode): bigint {
  if (denominator < 0n) {
    numerator = -numerator
    denominator = -denominator
  }
  const negative = numerator < 0n
  const magnitude = negative ? -numerator : numerator
  const quotient = magnitude / denominator
  const twiceRemainder = (magnitude % denominator) * 2n

  let rounded = quotient
  if (twiceRemainder > denominator) {
    rounded += 1n
  } else if (twiceRemainder === denominator) {
    const roundUp = mode === "half-up" || quotient % 2n === 1n
    if (roundUp) rounded += 1n
  }
  return negative ? -rounded : rounded
}

/**
 * Multiplies by the exact ratio `numerator / denominator`, e.g. `(1, 11)` for
 * the GST component of a GST-inclusive price.
 */
export function multiplyByRatio(
  value: Money,
  numerator: number,
  denominator: number,
  mode: RoundingMode = "half-up",
): Money {
  assertInteger(numerator, "Numerator")
  assertInteger(denominator, "Denominator")
  if (denominator === 0) {
    throw new InvalidMoneyError("Denominator must not be zero")
  }
  const result = divideRounded(BigInt(value.amount) * BigInt(numerator), BigInt(denominator), mode)
  return money(Number(result), value.currency)
}

/**
 * Splits a value by integer weights using the largest-remainder method.
 * The parts always sum to the original; leftover minor units go to the parts
 * with the largest fractional share (earlier parts win ties).
 */
export function allocate(value: Money, weights: readonly number[]): Money[] {
  if (weights.length === 0) {
    throw new InvalidMoneyError("At least one weight is required")
  }
  for (const weight of weights) {
    assertInteger(weight, "Weight")
    if (weight < 0) throw new InvalidMoneyError("Weights must not be negative")
  }
  const total = weights.reduce((acc, weight) => acc + BigInt(weight), 0n)
  if (total === 0n) {
    throw new InvalidMoneyError("Weights must not all be zero")
  }

  const sign = value.amount < 0 ? -1n : 1n
  const magnitude = BigInt(Math.abs(value.amount))

  const shares = weights.map((weight, index) => {
    const exact = magnitude * BigInt(weight)
    return { index, base: exact / total, remainder: exact % total }
  })

  let leftover = magnitude - shares.reduce((acc, share) => acc + share.base, 0n)
  const byRemainder = shares.toSorted((a, b) =>
    a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1,
  )
  for (const share of byRemainder) {
    if (leftover === 0n) break
    share.base += 1n
    leftover -= 1n
  }

  return shares.map((share) => money(Number(share.base * sign), value.currency))
}

const DECIMAL_PATTERN = /^(-)?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?$/

/**
 * Parses a plain decimal string such as `"1,234.50"` or `"-7"`.
 * Currency symbols, exponents and extra decimals are rejected rather than guessed.
 */
export function parseMoney(input: string, currency: CurrencyCode): Money {
  const match = DECIMAL_PATTERN.exec(input.trim())
  if (!match) {
    throw new InvalidMoneyError(`Not a decimal amount: "${input}"`)
  }
  const [, minus, whole = "0", fraction = ""] = match
  const exponent = currencyExponent(currency)
  if (fraction.length > exponent) {
    throw new InvalidMoneyError(`${currency} allows at most ${exponent} decimal places: "${input}"`)
  }
  const minorUnits = BigInt(whole.replaceAll(",", "") + fraction.padEnd(exponent, "0"))
  const amount = Number(minus ? -minorUnits : minorUnits)
  return money(amount, currency)
}

/** Machine-readable decimal string, e.g. `"-1234.56"`. Suitable for storage and CSV. */
export function toDecimalString(value: Money): string {
  const exponent = currencyExponent(value.currency)
  const digits = Math.abs(value.amount)
    .toString()
    .padStart(exponent + 1, "0")
  const whole = digits.slice(0, digits.length - exponent)
  const fraction = digits.slice(digits.length - exponent)
  const sign = value.amount < 0 ? "-" : ""
  return exponent === 0 ? `${sign}${whole}` : `${sign}${whole}.${fraction}`
}

/** Human-readable, locale-aware formatting for display only. */
export function formatMoney(value: Money, locale: string): string {
  const exponent = currencyExponent(value.currency)
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: value.currency,
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  }).format(Number(toDecimalString(value)))
}
